import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import defaultimg from '../../../assets/defaultimg.webp';
import { calculateDateDifference } from '../../General/Functions/datedifference';
import './projectlistcontent.css';

const API_URL = process.env.REACT_APP_API_URL;

const ProjectListContent = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/projectlist?sort=${sortBy}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      
      // Fetch project tags for each project
      const projectsWithTags = await Promise.all(
        data.map(async (project) => {
          const tagsResponse = await fetch(`${API_URL}/editproject/${project.project_id}/tags`);
          if (!tagsResponse.ok) throw new Error('Failed to fetch tags');
          const tagsData = await tagsResponse.json();
          return {
            ...project,
            tags: tagsData.map(tag => tag.tag_name)
          };
        })
      );

      setProjects(projectsWithTags);
      setFilteredProjects(projectsWithTags);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [sortBy]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProjects(projects);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const searchResults = projects.filter(project => {
      // Search in title and details
      const titleMatch = project.title.toLowerCase().includes(searchTermLower);
      const detailsMatch = project.details?.toLowerCase().includes(searchTermLower);
      
      // Search in tags
      const tagMatch = project.tags?.some(tag => 
        tag.toLowerCase().includes(searchTermLower)
      );

      return titleMatch || detailsMatch || tagMatch;
    });
    
    setFilteredProjects(searchResults);
  }, [searchTerm, projects]);

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleSearch = (event) => {
    event.preventDefault();
  };

  const getSortedProjects = () => {
    const sortedProjects = [...filteredProjects];
    switch (sortBy) {
      case 'newest':
        return sortedProjects.sort((a, b) => 
          new Date(b.creation_date) - new Date(a.creation_date)
        );
      case 'oldest':
        return sortedProjects.sort((a, b) => 
          new Date(a.creation_date) - new Date(b.creation_date)
        );
      case 'likes':
        return sortedProjects.sort((a, b) => b.likes - a.likes);
      default:
        return sortedProjects;
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const sortedProjects = getSortedProjects();

  return (
    <div className="projectlistbody">
      <div className="projectlist-grid-container">
        <div className="projectlist-grid-container2">
          <div className="projectlist-grid-item projectlist-grid-1">
            <h3 style={{ fontWeight: '400' }}>Project list</h3>
            <form className='projectlistsearchForm' onSubmit={handleSearch}>
              <input 
                type="text" 
                className='projectlistsearchBar' 
                placeholder="Search projects by title, description, or tags" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                required
              />
              <button type="submit" className='projectlistsearchBtn'>Go</button>
            </form>
          </div>
        </div>

        <div className="projectlist-grid-item projectlist-grid-3">
          <div className="projectlistresultstitle">
            <h2 className="projectlistresultheader">
              Project Results ({filteredProjects.length})
            </h2>
            <div className="projectlistsorting">
              <span>Sort by: </span>
              <select 
                className="projectroleselector" 
                value={sortBy}
                onChange={handleSortChange}
              >
                <option value="newest">Date (Newest First)</option>
                <option value="oldest">Date (Oldest First)</option>
                <option value="likes">Most Liked</option>
              </select>
            </div>
          </div>
          <hr />

          {sortedProjects.length === 0 ? (
            <div className="no-results">No projects found matching your search.</div>
          ) : (
            sortedProjects.map((project) => (
              <div key={project.project_id} className="projectbarresult-grid-container">
                <div className="projectbarresult-grid-1">
                  <img 
                    src={project.image || defaultimg} 
                    alt="Project" 
                    className="projectimg" 
                  />
                </div>

                <div className="projectbarresult-flex">
                  <RouterLink 
                    to={`/project/${project.project_id}`} 
                    className="projectbarresult-grid-2"
                  >
                    <h3>{project.title}</h3>
                  </RouterLink>

                  <div className="projectbarresult-grid-3">
                    <span className={`projlistphasestatus ${
                      project.phase === 'Onboarding' ? 'projlistphaseonboarding' :
                      project.phase === 'Initiation' ? 'projlistphaseinitiation' :
                      project.phase === 'Execution' ? 'projlistphaseexecution' :
                      project.phase === 'Closed' ? 'projlistphaseclosed' : ''
                    }`}>
                      {project.phase}
                    </span>
                    <span>Recruiting:</span>
                    <span className="projrecruiting">
                      {project.recruiting ? ' Open' : ' Closed'}
                    </span>
                  </div>

                  <div className="projectbarresult-grid-4">
                    Posted {calculateDateDifference(project.creation_date)}
                  </div>

                  <div className="projectbarresult-grid-5">
                    {project.details && (
                      <span>{project.details.substring(0, 150)}...</span>
                    )}
                  </div>

                  {project.tags && project.tags.length > 0 && (
                    <div className="projectbarresult-tags">
                      {project.tags.map((tag, index) => (
                        <span key={index} className="project-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="projectbarresult-grid-6">
                    <i className="fa-regular fa-heart" style={{ marginLeft: '1rem', marginRight: '0.3rem' }}></i>
                    <span>{project.likes || 0}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectListContent;